<script setup>
import { computed } from 'vue';
import BaseBubble from './Base.vue';
import { useI18n } from 'vue-i18n';
import { CSAT_RATINGS, CSAT_DISPLAY_TYPES } from 'shared/constants/messages';
import { MESSAGE_VARIANTS } from '../constants';
import { useMessageContext } from '../provider.js';

const { contentAttributes, content, variant } = useMessageContext();
const { t } = useI18n();

const response = computed(() => {
  return contentAttributes.value?.submittedValues?.csatSurveyResponse ?? {};
});

const isRatingSubmitted = computed(() => {
  return !!response.value.rating;
});

const displayType = computed(() => {
  return contentAttributes.value?.displayType || CSAT_DISPLAY_TYPES.EMOJI;
});

const isStarRating = computed(() => {
  return displayType.value === CSAT_DISPLAY_TYPES.STAR;
});

const rating = computed(() => {
  if (isRatingSubmitted.value) {
    return CSAT_RATINGS.find(
      csatOption => csatOption.value === response.value.rating
    );
  }

  return null;
});

const starRatingValue = computed(() => {
  return response.value.rating || 0;
});

const darkSurfaceVariants = [
  MESSAGE_VARIANTS.AGENT,
  MESSAGE_VARIANTS.BOT,
  MESSAGE_VARIANTS.TEMPLATE,
];

const isDarkSurfaceVariant = computed(() =>
  darkSurfaceVariants.includes(variant.value)
);

const headingTextClass = computed(() => {
  return isDarkSurfaceVariant.value ? 'text-white' : 'text-n-slate-12';
});

const labelTextClass = computed(() => {
  return isDarkSurfaceVariant.value ? 'text-white/70' : 'text-n-slate-11';
});

const bodyTextClass = computed(() => {
  return isDarkSurfaceVariant.value ? 'text-white' : 'text-n-slate-12';
});

const starInactiveClass = computed(() => {
  return isDarkSurfaceVariant.value ? 'text-white/40' : 'text-n-slate-10';
});
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="csat">
    <h4 :class="headingTextClass">
      {{ content || t('CONVERSATION.CSAT_REPLY_MESSAGE') }}
    </h4>
    <dl v-if="isRatingSubmitted" class="mt-4">
      <dt class="italic" :class="[labelTextClass]">
        {{ t('CONVERSATION.RATING_TITLE') }}
      </dt>
      <dd v-if="!isStarRating" :class="bodyTextClass">
        {{ t(rating.translationKey) }}
      </dd>
      <dd v-else class="flex mt-1" :class="[bodyTextClass]">
        <span v-for="n in 5" :key="n" class="text-2xl mr-1">
          <i
            :class="[
              n <= starRatingValue
                ? 'i-ri-star-fill text-n-amber-9'
                : 'i-ri-star-line',
              n > starRatingValue ? starInactiveClass : '',
            ]"
          />
        </span>
      </dd>

      <dt
        v-if="response.feedbackMessage"
        class="italic mt-2"
        :class="[labelTextClass]"
      >
        {{ t('CONVERSATION.FEEDBACK_TITLE') }}
      </dt>
      <dd :class="bodyTextClass">{{ response.feedbackMessage }}</dd>
    </dl>
  </BaseBubble>
</template>
